import { useAuth } from '../../../../util/authenticationProvider';
import { useEffect, useState, useRef, useMemo, useCallback, type FormEvent, type MutableRefObject } from 'react';
import axios from 'axios';
import { Alert, Box, Button, Checkbox, Container, CssBaseline, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, TextField, ThemeProvider, Typography, useTheme } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import DeleteIcon from '@mui/icons-material/Delete';
import { PrizeDto, Lottery } from 'src/util/types';
import config from '../../../../config';
import LotteriesList from './LotteriesList';
import { useTranslation } from 'react-i18next';
import LotteryEdit from '../LotteryEdit';

type PrizeSelection = {
  id: string;
  amount: number;
};

export default function LotteryManagement() {
  const authContext = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const theme = useTheme();
  const [prizeList, setPrizeList] = useState<PrizeDto[]>([]);
  const [selectedPrizes, setSelectedPrizes] = useState<PrizeSelection[]>([]);
  const [defaultSelectedPrizes, setDefaultSelectedPrizes] = useState<PrizeSelection[]>([]);
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Popup state
  const [open, setOpen] = useState(false);

  // Default lottery popup state
  const [defaultOpen, setDefaultOpen] = useState(false);
  const [defaultLottery, setDefaultLottery] = useState<any>(null);
  // Create default lottery popup state
  const [createDefaultOpen, setCreateDefaultOpen] = useState(false);
  const [isDefault, setIsDefault] = useState(true);

  // Add pagination state
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalLotteries, setTotalLotteries] = useState<number>(0);

  // For stable rowCount during loading
  const rowCountRef = useRef(totalLotteries);

  const rowCount = useMemo(() => {
    if (totalLotteries !== undefined) {
      rowCountRef.current = totalLotteries;
    }
    return rowCountRef.current;
  }, [totalLotteries]);

  const accessToken = authContext.tokens?.accessToken ?? '';
  const prizeMap = useMemo(() => new Map(prizeList.map((prize) => [prize.id.toString(), prize])), [prizeList]);

  type DateInputElement = HTMLInputElement & { showPicker?: () => void };
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const endDateInputRef = useRef<DateInputElement | null>(null);
  const [defaultStartDate, setDefaultStartDate] = useState('');
  const [defaultEndDate, setDefaultEndDate] = useState('');
  const defaultEndDateInputRef = useRef<DateInputElement | null>(null);

  const formatDateForInput = useCallback((input: string | Date) => {
    const date = typeof input === 'string' ? new Date(input) : input;
    if (Number.isNaN(date.getTime())) {
      return typeof input === 'string' ? input : '';
    }
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }, []);

  const minStartDate = formatDateForInput(new Date());
  const minEndDate = useMemo(() => {
    const base = startDate ? new Date(startDate) : new Date();
    const min = new Date(base.getTime() + 60 * 1000);
    return formatDateForInput(min);
  }, [formatDateForInput, startDate]);

  const minDefaultEndDate = useMemo(() => {
    const base = defaultStartDate ? new Date(defaultStartDate) : new Date();
    const min = new Date(base.getTime() + 60 * 1000);
    return formatDateForInput(min);
  }, [defaultStartDate, formatDateForInput]);

  const focusEndDateInput = useCallback((ref: MutableRefObject<DateInputElement | null>) => {
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.showPicker?.();
    });
  }, []);

  const syncSelections = useCallback((ids: string[], current: PrizeSelection[]) => {
    const currentMap = new Map(current.map((entry) => [entry.id, entry]));
    return ids.map((id) => currentMap.get(id) ?? { id, amount: 1 });
  }, []);

  const handlePrizeSelectChange = useCallback((event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const ids = typeof value === 'string' ? value.split(',') : value;
    setSelectedPrizes((prev) => syncSelections(ids, prev));
  }, [syncSelections]);

  const handleDefaultPrizeSelectChange = useCallback((event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const ids = typeof value === 'string' ? value.split(',') : value;
    setDefaultSelectedPrizes((prev) => syncSelections(ids, prev));
  }, [syncSelections]);

  const handlePrizeAmountChange = useCallback((id: string, amount: number, isDefault = false) => {
    const updater = isDefault ? setDefaultSelectedPrizes : setSelectedPrizes;
    updater((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, amount: amount < 0 ? 0 : amount } : entry))
    );
  }, []);

  const handlePrizeDelete = useCallback((id: string, isDefault = false) => {
    const updater = isDefault ? setDefaultSelectedPrizes : setSelectedPrizes;
    updater((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const handleStartDateChange = useCallback((value: string) => {
    setStartDate(value);
    setEndDate('');
    focusEndDateInput(endDateInputRef);
  }, [focusEndDateInput, endDateInputRef]);

  const handleDefaultStartDateChange = useCallback((value: string) => {
    setDefaultStartDate(value);
    setDefaultEndDate('');
    focusEndDateInput(defaultEndDateInputRef);
  }, [focusEndDateInput, defaultEndDateInputRef]);

  const getPrizeList = useCallback(async () => {
    if (!accessToken) {
      setPrizeList([]);
      setSelectedPrizes([]);
      setDefaultSelectedPrizes([]);
      return;
    }
    try {
      const response = await axios.get(config.apiUrl + "/api/prize/all", {
        headers: {
          "Authorization": "Bearer " + accessToken
        }
      });
      const list: PrizeDto[] = response.data.content;
      setPrizeList(list);
      setSelectedPrizes((prev) => prev.filter((entry) => list.some((prize) => prize.id.toString() === entry.id)));
      setDefaultSelectedPrizes((prev) => prev.filter((entry) => list.some((prize) => prize.id.toString() === entry.id)));
    } catch (err) {
      console.error(err);
    }
  }, [accessToken]);

  // Update getLotteries to use paged endpoint
  const getLotteries = useCallback(async (page = 0, pageSize = 10) => {
    if (!accessToken) {
      setLotteries([]);
      setTotalLotteries(0);
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(config.apiUrl + `/api/lottery/all/paged?page=${page}&size=${pageSize}&sort=createdAt,desc`, {
        headers: {
          "Authorization": "Bearer " + accessToken
        }
      });
      setLotteries(res.data.content.content); // Page<LotteryDto> is in content.content
      setTotalLotteries(res.data.content.totalElements);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  const validateDateRange = useCallback((startValue: string, endValue: string) => {
    if (!startValue || !endValue) {
      setError('Start and end dates are required');
      return false;
    }
    const startTime = new Date(startValue).getTime();
    const endTime = new Date(endValue).getTime();
    if (Number.isNaN(startTime) || Number.isNaN(endTime)) {
      setError('Invalid date selection');
      return false;
    }
    if (endTime <= startTime) {
      setError('End date must be after the start date');
      return false;
    }
    return true;
  }, [setError]);

  const handleSubmitData = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const formData = new FormData(event.currentTarget);
    const activationThresholdRaw = formData.get("activationThreshold");
    const activationThreshold = activationThresholdRaw ? Number(activationThresholdRaw) : 0;
    const startDateValue = formData.get("startDate")?.toString() ?? '';
    const endDateValue = formData.get("endDate")?.toString() ?? '';
    if (!validateDateRange(startDateValue, endDateValue)) {
      return;
    }

    const prizePayload = selectedPrizes.map((entry) => ({
      prize: { id: entry.id },
      amount: Number.isNaN(entry.amount) ? 0 : entry.amount
    }));

    const bodyData = {
      startDate: startDateValue,
      endDate: endDateValue,
      name: formData.get("name"),
      activationThreshold: Number.isNaN(activationThreshold) ? 0 : activationThreshold,
      prizes: prizePayload
    }
    axios.post(config.apiUrl + "/api/lottery/create", bodyData, {
      headers: {
        'Authorization': 'Bearer ' + accessToken
      }
    })
      .then(res => {
        setSuccess('Creation successful.');
        setLotteries([...lotteries, res.data.content]);
        setOpen(false);
        getLotteries();
      })
      .catch(() => {
        setError('Connection to Server failed.')
      })
  }

  // Create default lottery submit
  const handleSubmitDefaultData = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const formData = new FormData(event.currentTarget);
    const activationThresholdRaw = formData.get("activationThreshold");
    const activationThreshold = activationThresholdRaw ? Number(activationThresholdRaw) : 0;
    const startDateValue = formData.get("startDate")?.toString() ?? '';
    const endDateValue = formData.get("endDate")?.toString() ?? '';
    if (!validateDateRange(startDateValue, endDateValue)) {
      return;
    }

    const prizePayload = defaultSelectedPrizes.map((entry) => ({
      prize: { id: entry.id },
      amount: Number.isNaN(entry.amount) ? 0 : entry.amount
    }));

    const bodyData = {
      startDate: startDateValue,
      endDate: endDateValue,
      name: formData.get("name"),
      prizes: prizePayload,
      activationThreshold: Number.isNaN(activationThreshold) ? 0 : activationThreshold,
      default: isDefault
    }
    axios.post(config.apiUrl + "/api/lottery/create", bodyData, {
      headers: {
        'Authorization': 'Bearer ' + accessToken
      }
    })
      .then(res => {
        setSuccess('Default Lottery created.');
        setLotteries([...lotteries, res.data.content]);
        setCreateDefaultOpen(false);
        getLotteries();
      })
      .catch(() => {
        setError('Connection to Server failed.')
      })
  }

  const handleDefaultOpen = async () => {
    try {
      const response = await axios.get(config.apiUrl + "/api/lottery/default", {
        headers: {
          "Authorization": "Bearer " + accessToken
        }
      });
      const data = response.data.content;
      if (data) {
        setDefaultLottery(data);
      } else {
        setDefaultLottery(null);
      }
      setDefaultOpen(true);
    } catch {
      setDefaultLottery(null);
      setDefaultOpen(true);
    }
  };

  const handleDefaultClose = () => {
    setDefaultOpen(false);
    getLotteries();
  };

  const handleCreateFromDefault = () => {
    setDefaultOpen(false);
    setCreateDefaultOpen(true);
  };

  useEffect(() => {
    getPrizeList();
  }, [getPrizeList]);

  useEffect(() => {
    getLotteries(paginationModel.page, paginationModel.pageSize);
  }, [getLotteries, paginationModel.page, paginationModel.pageSize]);

  // DataGrid pagination handler
  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
  };

  const handleOpen = () => {
    setSelectedPrizes([]);
    setStartDate('');
    setEndDate('');
    setError('');
    setSuccess('');
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setError('');
    setSuccess('');
    setSelectedPrizes([]);
    setStartDate('');
    setEndDate('');
  };

  const handleCreateDefaultOpen = () => {
    setIsDefault(true);
    setDefaultSelectedPrizes([]);
    setDefaultStartDate('');
    setDefaultEndDate('');
    setError('');
    setSuccess('');
    setCreateDefaultOpen(true);
  };

  const handleCreateDefaultClose = () => {
    setCreateDefaultOpen(false);
    setError('');
    setSuccess('');
    setDefaultSelectedPrizes([]);
    setDefaultStartDate('');
    setDefaultEndDate('');
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="lg">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{ mb: 2 }}
              onClick={handleOpen}
            >
              {t('lottery.createLottery')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              sx={{ mb: 2 }}
              onClick={handleDefaultOpen}
            >
              {t('lottery.editDefaultLottery')}
            </Button>
            <Button
              variant="contained"
              color="success"
              sx={{ mb: 2 }}
              onClick={handleCreateDefaultOpen}
            >
              {t('lottery.createDefaultLottery')}
            </Button>
            <Dialog open={defaultOpen} onClose={handleDefaultClose} maxWidth="md" fullWidth>
              <DialogTitle>{t('lottery.editDefaultLottery')}</DialogTitle>
              <DialogContent>
                {defaultLottery ? (
                <LotteryEdit {...defaultLottery} />
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('lottery.noDefaultLottery')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleCreateFromDefault}
                    >
                      {t('lottery.createDefaultLottery')}
                    </Button>
                  </Box>
                )}
              </DialogContent>
            </Dialog>
            <Dialog open={createDefaultOpen} onClose={handleCreateDefaultClose} maxWidth="md" fullWidth>
              <DialogTitle>{t('lottery.createDefaultLottery')}</DialogTitle>
              <DialogContent>
                <Box component="form" onSubmit={handleSubmitDefaultData} noValidate sx={{ mt: 1 }}>
                  <TextField
                    margin="normal"
                    fullWidth
                    id="name"
                    label={t('field.Name')}
                    name="name"
                    autoComplete="name"
                    autoFocus
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    margin="normal"
                    type="datetime-local"
                    required
                    fullWidth
                    id="startDate"
                    name="startDate"
                    label={t('field.StartDate')}
                    value={defaultStartDate}
                    onChange={(e) => handleDefaultStartDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minStartDate }}
                  />
                  <TextField
                    margin="normal"
                    type="datetime-local"
                    required
                    fullWidth
                    name="endDate"
                    id="endDate"
                    label={t('field.EndDate')}
                    value={defaultEndDate}
                    onChange={(e) => setDefaultEndDate(e.target.value)}
                    inputRef={defaultEndDateInputRef}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minDefaultEndDate }}
                  />
                  <TextField
                    margin="normal"
                    fullWidth
                    name="activationThreshold"
                    id="activationThreshold"
                    label={t('lottery.activationThreshold', { defaultValue: 'Activation Threshold' })}
                    type="number"
                    inputProps={{ min: 0 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isDefault}
                        onChange={e => setIsDefault(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('lottery.default')}
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="default-prize-select-label">
                      {t('lottery.prizeSelectLabel', { defaultValue: 'Select prizes' })}
                    </InputLabel>
                    <Select
                      labelId="default-prize-select-label"
                      id="default-prize-select"
                      multiple
                      label={t('lottery.prizeSelectLabel', { defaultValue: 'Select prizes' })}
                      value={defaultSelectedPrizes.map((entry) => entry.id)}
                      onChange={handleDefaultPrizeSelectChange}
                      renderValue={(selected) => (selected as string[])
                        .map((id) => prizeMap.get(id)?.name ?? '')
                        .filter(Boolean)
                        .join(', ')}
                    >
                      {prizeList.map((prize) => (
                        <MenuItem key={prize.id} value={prize.id.toString()}>
                          {prize.name || prize.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {defaultSelectedPrizes.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('lottery.noPrizesSelected', { defaultValue: 'No prizes selected yet.' })}
                      </Typography>
                    )}
                    {defaultSelectedPrizes.map((entry) => {
                      const prize = prizeMap.get(entry.id);
                      return (
                        <Grid container spacing={2} alignItems="center" key={entry.id}>
                          <Grid item xs={6}>
                            <Typography>{prize?.name || prize?.description || entry.id}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              type="number"
                              fullWidth
                              label={t('lottery.amountLabel', { defaultValue: 'Amount' })}
                              inputProps={{ min: 0 }}
                              value={entry.amount}
                              onChange={(event) =>
                                handlePrizeAmountChange(entry.id, Number(event.target.value) || 0, true)
                              }
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton
                              aria-label={t('general.Delete')}
                              onClick={() => handlePrizeDelete(entry.id, true)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Stack>
                  <DialogActions sx={{ px: 0 }}>
                    <Button onClick={handleCreateDefaultClose}>{t('general.Cancel')}</Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                    >
                      {t('lottery.createDefaultLottery')}
                    </Button>
                  </DialogActions>
                  {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : ''}
                  {success ? <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert> : ''}
                </Box>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
              <DialogTitle>{t('lottery.createLottery')}</DialogTitle>
              <DialogContent>
                <Box component="form" onSubmit={handleSubmitData} noValidate sx={{ mt: 1 }}>
                  <TextField
                    margin="normal"
                    fullWidth
                    id="name"
                    label={t('field.Name')}
                    name="name"
                    autoComplete="name"
                    autoFocus
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    margin="normal"
                    type="datetime-local"
                    required
                    fullWidth
                    id="startDate"
                    name="startDate"
                    label={t('field.StartDate')}
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minStartDate }}
                  />
                  <TextField
                    margin="normal"
                    type="datetime-local"
                    required
                    fullWidth
                    name="endDate"
                    id="endDate"
                    label={t('field.EndDate')}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    inputRef={endDateInputRef}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minEndDate }}
                  />
                  <TextField
                    margin="normal"
                    fullWidth
                    name="activationThreshold"
                    id="activationThreshold"
                    label={t('lottery.activationThreshold', { defaultValue: 'Activation Threshold' })}
                    type="number"
                    inputProps={{ min: 0 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="prize-select-label">
                      {t('lottery.prizeSelectLabel', { defaultValue: 'Select prizes' })}
                    </InputLabel>
                    <Select
                      labelId="prize-select-label"
                      id="prize-select"
                      multiple
                      label={t('lottery.prizeSelectLabel', { defaultValue: 'Select prizes' })}
                      value={selectedPrizes.map((entry) => entry.id)}
                      onChange={handlePrizeSelectChange}
                      renderValue={(selected) => (selected as string[])
                        .map((id) => prizeMap.get(id)?.name ?? '')
                        .filter(Boolean)
                        .join(', ')}
                    >
                      {prizeList.map((prize) => (
                        <MenuItem key={prize.id} value={prize.id.toString()}>
                          {prize.name || prize.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {selectedPrizes.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('lottery.noPrizesSelected', { defaultValue: 'No prizes selected yet.' })}
                      </Typography>
                    )}
                    {selectedPrizes.map((entry) => {
                      const prize = prizeMap.get(entry.id);
                      return (
                        <Grid container spacing={2} alignItems="center" key={entry.id}>
                          <Grid item xs={6}>
                            <Typography>{prize?.name || prize?.description || entry.id}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              type="number"
                              fullWidth
                              label={t('lottery.amountLabel', { defaultValue: 'Amount' })}
                              inputProps={{ min: 0 }}
                              value={entry.amount}
                              onChange={(event) =>
                                handlePrizeAmountChange(entry.id, Number(event.target.value) || 0, false)
                              }
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton
                              aria-label={t('general.Delete')}
                              onClick={() => handlePrizeDelete(entry.id, false)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Stack>

                  <DialogActions sx={{ px: 0 }}>
                    <Button onClick={handleClose}>{t('general.Cancel')}</Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      {t('lottery.createLottery')}
                    </Button>
                  </DialogActions>
                  {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : ''}
                  {success ? <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert> : ''}
                </Box>
              </DialogContent>
            </Dialog>
          </Box>
          <Box>
            <LotteriesList
              rows={lotteries}
              paginationModel={paginationModel}
              rowCount={rowCount}
              onPaginationModelChange={handlePaginationModelChange}
              paginationMode="server"
              loading={isLoading}
            />
          </Box>
        </Container>
      </ThemeProvider>
    </>
  );
}
